import {defineStore} from 'pinia'
import {ErrorLocationType, ErrorType, PopupType, PreferenceType, SsoType} from "@/utilities/system/enums/Enums";
import FilterService from "@/utilities/system/services/FilterService";
import {ColumnStore, Store} from "@bryntum/grid";
import {UserService} from "@/utilities/services/entities/UserService";
import {SystemCompanySettings} from "@/models/entities/settings/SystemCompanySettings";
import {StartupConfig} from "@/models/system/StartupConfig";
import {UserPreference} from "@/models/system/user/UserPreference";
import {User} from "@/models/entities/users/User";
import {featureColumn} from "@/AppConfig";
import NullOrUndefinedHelper from "@/utilities/system/helpers/NullOrUndefinedHelper";
import {AttributeFilter} from "@/models/AttributeFilter";
import router from "@/router";

export const useSystemStore = defineStore('SystemStore', {
    state: () => {
        return {
            refreshIsActive: true,
            gridDataIsLoading: false,
            currentItems: 0,
            allItems: 0,
            layout: "simple-layout", //"app-layout",
            success: {
                show: false,
                text: "Gespeichert!",
            },
            error: {
                location: ErrorLocationType.none,
                type: ErrorType.warning,
                text: "Fehler!",
            },
            modal: {
                show: false,
                validator: { $validate:()=>{} },
                resetButton: false,
                title: "Default Popup Title",
                popupType: PopupType.none,
                contentObject: {},
                action: ()=>{},
                bigMode: false,
            },
            gridStore: null as Store|null,
            gridColumnStore: null as ColumnStore|null,
            userPreferences: {
                userFilters: new Array<any>(),
                userHiddenColumns: new Array<any>(),
                userColumnOrder: new Array<any>(),
                general: {
                    darkMode: false,
                    userStartPage: null,
                    userLocale: null,
                },
                refreshCurrentPage: true,
            },
            tmpPreferences: {
                tmpUserFilters: new Array<any>(),
                tmpUserHiddenColumns: new Array<any>(),
            },
            countAfterFilters: 0,
            user: null as User | null,
            oAuth:{
                routeAfterLogin: null as string|null
            },
            settings: {
                notifications: {
                    durationSuccess: 10000,
                    durationWarning: 10000,
                    durationError: -1,
                    durationInfo: 10000,
                },
                defaultHiddenColumns: null as Array<UserPreference>|null,
                systemCompanySettings: null as SystemCompanySettings|null,
                startupConfig: null as StartupConfig|null,
                enabledFeatures: null as Array<string>|null,
            },
            uploadedFile: null as File|null,
        }

    },
    getters: {
    },
    actions: {
        getCallbackUrls():{normal:string, silent:string} {
            const oauthRoutes = router.getRoutes().filter(route=> (route.name as string).toLowerCase().includes("oauth"));
            const silentRoute = oauthRoutes.filter(route => (route.name as string).toLowerCase().includes("silent"))[0];
            const normalRoute = oauthRoutes.filter(route => route != silentRoute)[0];

            return {normal:normalRoute.path, silent:silentRoute.path};
        },
        async updateTablePreferences({ userId, tableName }:any) {
            const tmpUserFilters = this.tmpPreferences.tmpUserFilters;
            const filteredCurrentFilters = this.userPreferences.userFilters.filter((f:any) => f.tableName !== tableName);

            const tmpHiddenColumns = this.tmpPreferences.tmpUserHiddenColumns;
            const filteredCurrentHiddenColumns = this.userPreferences.userHiddenColumns.filter((f:any) => f.tableName !== tableName);

            // update preferences in store
            this.setUserPreferences({
                userFilters: tmpUserFilters.concat(filteredCurrentFilters),
                userHiddenColumns: tmpHiddenColumns.concat(filteredCurrentHiddenColumns),
                userColumnOrder: this.userPreferences.userColumnOrder,
                general: {
                    darkMode: this.userPreferences.general.darkMode,
                    userStartPage: this.userPreferences.general.userStartPage,
                    userLocale: this.userPreferences.general.userLocale,
                }
            });

            const userService = new UserService();

            // apply filters
            if(tmpUserFilters.length === 0) {
                this.gridStore?.removeFilter("userFilter");
            }
            else {
                this.gridStore?.filter({
                    id: "userFilter",
                    filterBy: FilterService.buildFilter(tableName)
                });
            }

            // apply column visibility
            // go through each column (visible or not)
            // @ts-expect-error
            for (const c of this.gridColumnStore?.storage.values) {
                const needsHiding = tmpHiddenColumns.some((thc:any) => thc.attributeName === c.data.field);

                if(needsHiding && !c.hidden) {
                    c.hide();
                }
                else if(!needsHiding && c.hidden && !featureColumn.includes(c.id)) {
                    c.show();
                }
            }

            // update userPreferences in Backend / delete all if array is empty (bool param)
            const filterResp = await userService.setUserPreferences(userId, PreferenceType.filter, tableName, tmpUserFilters.length === 0, tmpUserFilters, ErrorLocationType.popup);
            if (!filterResp) return;

            const hiddenColResp = await userService.setUserPreferences(userId, PreferenceType.hiddenColumn, tableName, tmpHiddenColumns.length === 0, tmpHiddenColumns, ErrorLocationType.popup);
            if (!hiddenColResp) return;

            // clear tmp data in store
            this.setTmpFilters([]);
            this.setTmpHiddenColumns([]);

            this.closeModal();
            this.showSuccess('Gespeichert');
        },
        toggleDarkMode () {
            this.userPreferences.general.darkMode = !this.userPreferences.general.darkMode;
        },
        resetState(){
            this.gridStore = new Store();
            this.gridColumnStore = null;

            this.modal = {
                show: false,
                validator: { $validate:()=>{} },
                resetButton: false,
                title: "Default Popup Title",
                popupType: PopupType.none,
                contentObject: {},
                action: ()=>{},
                bigMode: false
            }

            this.userPreferences = {
                userFilters: new Array<any>(),
                userHiddenColumns: new Array<any>(),
                userColumnOrder: new Array<any>(),
                general: {
                    darkMode: false,
                    userStartPage: null,
                    userLocale: this.userPreferences.general.userLocale,
                },
                refreshCurrentPage: true,
            };

            this.tmpPreferences = {
                tmpUserFilters: new Array<any>(),
                tmpUserHiddenColumns: new Array<any>(),
            };
        },
        reorderColumns(tableName:string, customStore:ColumnStore|null = null){
            if(!this.userPreferences.userColumnOrder?.some(()=>true)) return;

            const orderedColumns = this.userPreferences.userColumnOrder?.filter(oc => oc.tableName === tableName);
            for (const oc of orderedColumns) {
                this.moveColumnToIndex(+oc.attributeValue, oc.attributeName, customStore);
            }
        },
        moveColumnToIndex(index:number, columnId:string, customStore:ColumnStore|null = null){
            if(NullOrUndefinedHelper(this.gridColumnStore) && NullOrUndefinedHelper(customStore)) return;

            const columnStore = (customStore ?? this.gridColumnStore) as ColumnStore;
            const elementToMove = columnStore.getById(columnId);

            // bryntum uses the index of the array AFTER removing the moved column from said array, so remove the column, before inserting it at the new index...
            // small reminder: OVERTHINKING IS HARD...
            columnStore.remove(elementToMove);
            columnStore.insert(index, elementToMove);
        },
        openPopup ({data, action, resetButton, title, type, big = false}:any) {
            this.closeModal();
            this.modal = {
                validator: {$validate: ()=>true},
                show: true,
                title: title,
                resetButton: resetButton,
                popupType: type,
                contentObject: data,
                action: action,
                bigMode: big,
            }
        },
        closeModal () {
            this.modal.contentObject = {};
            this.modal.show = false;
        },
        showSuccess(text:string) {
            this.success.show = true;
            this.success.text = text === undefined ? "DEFAULT OK!" : text;
        },
        setGridStore (payload:Store|null) {
            this.gridStore = payload;
        },
        setGridColumnStore (payload:any) {
            this.gridColumnStore = payload;
        },
        setUserPreferences (payload: { userFilters:any, userHiddenColumns:any, general: { darkMode:any, userStartPage:any, userLocale:any }, userColumnOrder:any }|null) {
            if (payload === null) return;
            this.userPreferences.userFilters = payload.userFilters;
            this.userPreferences.userHiddenColumns = payload.userHiddenColumns;
            this.userPreferences.general = payload.general;
            this.userPreferences.userColumnOrder = payload.userColumnOrder;
        },
        async saveUserColumnOrder (tableName:string, columnId:string, newIndex:string) {
            if(this.user === null) return;

            const colmnOrder = new AttributeFilter(tableName, columnId, newIndex);

            // save/update userPreferences in db
            const userService = new UserService();
            const saveResp = await userService.setUserPreferences(this.user.id, PreferenceType.columnOrder, tableName, false, [colmnOrder], ErrorLocationType.table)
            if (!saveResp) return;

            // update global store preferences
            const filteredList = this.userPreferences.userColumnOrder?.filter(oc => oc.attributeValue === columnId);
            filteredList.push(colmnOrder);
            this.userPreferences.userColumnOrder = filteredList;
        },
        setTmpFilters(payload: any|null) {
            this.tmpPreferences.tmpUserFilters = payload;
        },
        setTmpHiddenColumns(payload: any|null) {
            this.tmpPreferences.tmpUserHiddenColumns = payload;
        },
        addUserFilter(payload: any|null) {
            if (payload !== null) {
                // check if dateValue is added and remove old one before adding new one
                if(payload.attributeName.endsWith("START") || payload.attributeName.endsWith("END"))
                    this.tmpPreferences.tmpUserFilters = this.tmpPreferences.tmpUserFilters.filter(tf => tf.attributeName !== payload.attributeName)

                // drop pushing values already present
                if(!this.tmpPreferences.tmpUserFilters.includes(payload))
                    this.tmpPreferences.tmpUserFilters.push(payload);
            }
        },
        addUserHiddenColumn(payload: any|null) {
            if (payload !== null) {
                this.tmpPreferences.tmpUserHiddenColumns.push(payload);
            }
        },
        setCountAfterFilters(payload: number) {
            this.countAfterFilters = payload;
        },
        setUser(payload:any|null){
            this.user = payload;
        },
        getAuthorityUrl():string {
            return this.settings.startupConfig?.idsUrl ?? "";
        }
    }
})
